import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import useToken from '../../hooks/useToken';
import DocIntroDialog from '../shared/DocIntroDialog';
import DRQuestionPanel from '../shared/DRQuestionPanel';
import DocViewerTourJoyride from '../shared/DocViewerTourJoyride';

const TABS = [
  { key: 'context',   label: 'C4 Context',         endpoint: 'design-document-context' },
  { key: 'container', label: 'C4 Container',        endpoint: 'design-document-container' },
  { key: 'component', label: 'C4 Component',        endpoint: 'design-document-component' },
  { key: 'uc',        label: 'Use Case Diagram',   endpoint: 'design-document-uc' },
];

// ── Zoomable SVG canvas ───────────────────────────────────────────────────────
function ZoomableSvgViewer({ svg }) {
  const viewportRef  = useRef(null);
  const liveRef      = useRef({ zoom: 1, panX: 0, panY: 0 });
  const isDragging   = useRef(false);
  const dragStart    = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  const [zoom, setZoom]       = useState(1);
  const [pan,  setPan]        = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);

  const applyTransform = useCallback((newZoom, newPan) => {
    const clamped = Math.min(4, Math.max(0.05, newZoom));
    liveRef.current = { zoom: clamped, panX: newPan.x, panY: newPan.y };
    setZoom(clamped);
    setPan(newPan);
  }, []);

  const fitToCanvas = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const svgEl = vp.querySelector('svg');
    if (!svgEl) return;
    let svgW, svgH;
    const vb = svgEl.getAttribute('viewBox');
    if (vb) {
      const p = vb.trim().split(/[\s,]+/);
      svgW = parseFloat(p[2]);
      svgH = parseFloat(p[3]);
    } else {
      svgW = parseFloat(svgEl.getAttribute('width'))  || 800;
      svgH = parseFloat(svgEl.getAttribute('height')) || 600;
    }
    const viewW = vp.clientWidth;
    const viewH = vp.clientHeight;
    const fit   = Math.min((viewW - 40) / svgW, (viewH - 40) / svgH);
    applyTransform(fit, {
      x: (viewW - svgW * fit) / 2,
      y: (viewH - svgH * fit) / 2,
    });
  }, [applyTransform]);

  // Fit when SVG loads or viewport resizes
  useEffect(() => {
    if (!svg) return;
    requestAnimationFrame(() => fitToCanvas());
  }, [svg, fitToCanvas]);

  // Scroll-to-zoom (non-passive so we can preventDefault)
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = e => {
      e.preventDefault();
      const { zoom: curZoom, panX, panY } = liveRef.current;
      const factor   = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      const newZoom  = Math.min(4, Math.max(0.05, curZoom * factor));
      const rect     = vp.getBoundingClientRect();
      const mx       = e.clientX - rect.left;
      const my       = e.clientY - rect.top;
      applyTransform(newZoom, {
        x: mx - (mx - panX) * (newZoom / curZoom),
        y: my - (my - panY) * (newZoom / curZoom),
      });
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [applyTransform]);

  const handleSlider = val => {
    const vp = viewportRef.current;
    const newZoom = val / 100;
    if (!vp) { applyTransform(newZoom, { x: liveRef.current.panX, y: liveRef.current.panY }); return; }
    const { zoom: curZoom, panX, panY } = liveRef.current;
    const cx = vp.clientWidth  / 2;
    const cy = vp.clientHeight / 2;
    applyTransform(newZoom, {
      x: cx - (cx - panX) * (newZoom / curZoom),
      y: cy - (cy - panY) * (newZoom / curZoom),
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div id="doc-zoom-controls" style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 12px', borderBottom: '1px solid var(--border)',
        background: 'var(--surface)', flexShrink: 0,
      }}>
        <button onClick={fitToCanvas} style={btnStyle}>Fit</button>
        <button onClick={() => {
          const { zoom: z, panX, panY } = liveRef.current;
          const vp = viewportRef.current;
          const cx = vp ? vp.clientWidth / 2 : 0;
          const cy = vp ? vp.clientHeight / 2 : 0;
          const nz = Math.min(4, z * 1.2);
          applyTransform(nz, { x: cx - (cx - panX) * (nz / z), y: cy - (cy - panY) * (nz / z) });
        }} style={btnStyle}>+</button>
        <button onClick={() => {
          const { zoom: z, panX, panY } = liveRef.current;
          const vp = viewportRef.current;
          const cx = vp ? vp.clientWidth / 2 : 0;
          const cy = vp ? vp.clientHeight / 2 : 0;
          const nz = Math.max(0.05, z / 1.2);
          applyTransform(nz, { x: cx - (cx - panX) * (nz / z), y: cy - (cy - panY) * (nz / z) });
        }} style={btnStyle}>−</button>
        <input
          type="range" min={5} max={400} step={1}
          value={Math.round(zoom * 100)}
          onChange={e => handleSlider(parseInt(e.target.value, 10))}
          style={{ width: 120, cursor: 'pointer', accentColor: 'var(--brand)' }}
        />
        <span style={{ minWidth: 40, fontSize: 13, color: 'var(--text-secondary)' }}>{Math.round(zoom * 100)}%</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>
          Scroll to zoom · Middle/right-drag to pan
        </span>
      </div>

      {/* Canvas */}
      <div
        id="doc-viewport"
        ref={viewportRef}
        style={{
          flex: 1,
          overflow: 'hidden',
          background: 'var(--surface-alt)',
          position: 'relative',
          cursor: panning ? 'grabbing' : 'default',
          userSelect: 'none',
        }}
        onContextMenu={e => { if (isDragging.current) e.preventDefault(); }}
        onMouseDown={e => {
          if (e.button !== 1 && e.button !== 2) return;
          e.preventDefault();
          isDragging.current = true;
          dragStart.current = { x: e.clientX, y: e.clientY, panX: liveRef.current.panX, panY: liveRef.current.panY };
          setPanning(true);
        }}
        onMouseMove={e => {
          if (!isDragging.current) return;
          applyTransform(liveRef.current.zoom, {
            x: dragStart.current.panX + (e.clientX - dragStart.current.x),
            y: dragStart.current.panY + (e.clientY - dragStart.current.y),
          });
        }}
        onMouseUp={e => { if (e.button === 1 || e.button === 2) { isDragging.current = false; setPanning(false); } }}
        onMouseLeave={() => { isDragging.current = false; setPanning(false); }}
      >
        <div
          dangerouslySetInnerHTML={{ __html: svg }}
          style={{
            position: 'absolute',
            transformOrigin: '0 0',
            transform: `translate(${pan.x}px,${pan.y}px) scale(${zoom})`,
            pointerEvents: 'none',
          }}
        />
      </div>
    </div>
  );
}

const btnStyle = {
  padding: '3px 10px', fontSize: 13, cursor: 'pointer',
  border: '1px solid var(--border)', borderRadius: 4,
  background: 'var(--surface-alt)', color: 'var(--text-primary)',
  lineHeight: 1.4,
};

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DocumentViewPage() {
  const { website_id }               = useParams();
  const { token, refreshAccessToken, getValidToken } = useToken();

  const [activeTab,         setActiveTab]         = useState(TABS[0].key);
  // { [tabKey]: 'loading' | 'error' | svgString }
  const [svgCache,          setSvgCache]          = useState({});
  const [globalError,       setGlobalError]       = useState(null);
  const [introOpen,         setIntroOpen]         = useState(false);
  const [threatModelProvided, setThreatModelProvided] = useState(false);
  const [joyrideRun,        setJoyrideRun]        = useState(false);
  const [tourKey,           setTourKey]           = useState(0);

  const authRef       = useRef(null);
  const loadedRef     = useRef(new Set());
  // Track when the current tab was activated for dwell logging
  const tabEnteredAt  = useRef(Date.now());

  const tourContext = new URLSearchParams(window.location.search).get('quiz') === '1' ? 'doc_review' : 'experiment';

  const logEvent = useCallback(async (type, details = {}) => {
    let auth = authRef.current;
    if (!auth) auth = await getValidToken().catch(() => null);
    if (!auth) return;
    fetch('/api/design-document/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
      body: JSON.stringify({ event: type, timestamp: new Date().toISOString(), ...details }),
    }).catch(() => {});
  }, [getValidToken]);

  const markTourSeen = useCallback(async () => {
    const auth = authRef.current || (await getValidToken().catch(() => null));
    if (!auth) return;
    fetch(`/api/website/${website_id}/design-document/tour-seen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
      body: JSON.stringify({ context: tourContext }),
    }).catch(() => {});
  }, [getValidToken, website_id, tourContext]);

  // Resolve auth once on mount
  useEffect(() => {
    (async () => {
      const auth = await getValidToken().catch(() => null);
      authRef.current = auth;
    })();
  }, [token, refreshAccessToken]);

  const loadTab = useCallback(async (tabKey) => {
    if (loadedRef.current.has(tabKey)) return;
    loadedRef.current.add(tabKey);

    setSvgCache(prev => ({ ...prev, [tabKey]: 'loading' }));

    const tab  = TABS.find(t => t.key === tabKey);
    const auth = authRef.current || await getValidToken().catch(() => null);
    if (!auth) { setSvgCache(prev => ({ ...prev, [tabKey]: 'error' })); return; }

    try {
      const res = await fetch(`/api/website/${website_id}/${tab.endpoint}`, {
        headers: { Authorization: `Bearer ${auth}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSvgCache(prev => ({ ...prev, [tabKey]: data.svg || 'error' }));
    } catch {
      setSvgCache(prev => ({ ...prev, [tabKey]: 'error' }));
    }
  }, [website_id, refreshAccessToken]);

  // Load first tab + log viewer open on mount
  useEffect(() => {
    (async () => {
      const auth = await getValidToken().catch(() => null);
      if (!auth) { setGlobalError('Authentication failed'); return; }
      authRef.current = auth;

      // Resolve threat_model_provided for the dialog, and whether the onboarding
      // tour has already been seen for this context
      fetch('/api/assignment', { headers: { Authorization: `Bearer ${auth}` } })
        .then(r => r.json())
        .then(list => {
          const a = list.find(w => String(w.website_id) === String(website_id));
          if (!a) return;
          setThreatModelProvided(!!a.threat_model_provided);
          const tourSeenAt = tourContext === 'doc_review'
            ? a.doc_tour_seen_doc_review_at
            : a.doc_tour_seen_experiment_at;
          if (!tourSeenAt) {
            markTourSeen();
            requestAnimationFrame(() => setJoyrideRun(true));
          }
        })
        .catch(() => {});

      await loadTab(TABS[0].key);
      logEvent('open', { page: 'DESIGN_DOCUMENT_VIEWER', website_id, initial_tab: TABS[0].key });
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [website_id]);

  // Visibility tracking
  useEffect(() => {
    const onVis = () => logEvent('visibility_change', { visible: !document.hidden, tab: activeTab });
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [logEvent, activeTab]);

  const handleTabChange = (newKey) => {
    if (newKey === activeTab) return;

    // Log dwell on the tab we're leaving
    const dwellSeconds = Math.round((Date.now() - tabEnteredAt.current) / 1000);
    logEvent('tab_dwell', { document_type: activeTab, dwell_seconds: dwellSeconds });

    // Activate new tab
    tabEnteredAt.current = Date.now();
    setActiveTab(newKey);
    loadTab(newKey);
    // 'open' is the event type the backend maps to DOCUMENT_VIEW_OPEN in the DB
    logEvent('open', { document_type: newKey });
  };

  if (globalError) {
    return <div style={{ padding: 24 }}><Alert variant="danger">{globalError}</Alert></div>;
  }

  const tabSvg = svgCache[activeTab];

  return (
    <div id="doc-viewer-root" style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'Inter, sans-serif' }}>
      <DocIntroDialog
        show={introOpen}
        onHide={() => setIntroOpen(false)}
        threatModelProvided={threatModelProvided}
      />
      <DocViewerTourJoyride key={tourKey} run={joyrideRun} onFinish={() => setJoyrideRun(false)} />

      {/* Tab bar */}
      <div id="doc-tab-bar" style={{
        display: 'flex', alignItems: 'stretch',
        borderBottom: '2px solid var(--border)',
        background: 'var(--surface)', flexShrink: 0,
        paddingLeft: 12,
      }}>
        <span style={{
          padding: '10px 14px', fontSize: 13, fontWeight: 700,
          color: 'var(--text-primary)', alignSelf: 'center', whiteSpace: 'nowrap',
        }}>
          Design Document Viewer
        </span>
        <button
          id="doc-intro-btn"
          onClick={() => setIntroOpen(true)}
          style={{
            ...btnStyle,
            flexShrink: 0, alignSelf: 'center',
            fontSize: 12, padding: '4px 12px',
          }}
        >
          📚 Doc Intro
        </button>
        <button
          onClick={() => { setTourKey(k => k + 1); setJoyrideRun(true); }}
          title="How to use this viewer"
          style={{
            ...btnStyle,
            flexShrink: 0, margin: '0 12px', alignSelf: 'center',
            fontSize: 12, padding: '4px 12px',
          }}
        >
          ? How to use
        </button>
        <div style={{ width: 1, background: 'var(--border)', margin: '6px 0' }} />
        <div style={{ display: 'flex', alignItems: 'stretch', overflowX: 'auto', flex: 1, paddingLeft: 12 }}>
          {TABS.map(tab => {
            const active = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                style={{
                  padding: '10px 18px',
                  fontSize: 13,
                  fontWeight: active ? 700 : 400,
                  color: active ? 'var(--brand)' : 'var(--text-secondary)',
                  background: 'none',
                  border: 'none',
                  borderBottom: active ? '2px solid var(--brand)' : '2px solid transparent',
                  marginBottom: -2,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'color 0.15s',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content area */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {!tabSvg || tabSvg === 'loading' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 24 }}>
            <Spinner animation="border" size="sm" />
            <span style={{ color: 'var(--text-secondary)' }}>Loading diagram…</span>
          </div>
        ) : tabSvg === 'error' ? (
          <div style={{ padding: 24 }}>
            <Alert variant="warning">Diagram not available for this website.</Alert>
          </div>
        ) : (
          <ZoomableSvgViewer key={activeTab} svg={tabSvg} />
        )}
      </div>

      <DRQuestionPanel category="design_doc" />
    </div>
  );
}
